import React, { useEffect, useRef } from 'react';

const lifterPinMap = [
  { width: 140, pins: 4 },
  { width: 150, pins: 4 },
  { width: 165, pins: 4 },
  { width: 200, pins: 5 },
  { width: 205, pins: 5 },
  { width: 210, pins: 5 },
  { width: 250, pins: 6 },
  { width: 300, pins: 6 },
];

const safeNumber = val => {
  const num = parseFloat(val);
  return isNaN(num) ? 0 : num;
};

const formatDate = dateStr => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-GB', {
    day:    '2-digit',
    month:  'short',
    year:   '2-digit',
  });
};

const getPinCount = width => {
  const item = lifterPinMap.find(x => x.width === width);
  return item ? item.pins : 0;
};

/**
 * Projects a reline date anchored at 12:00 PM local time.
 */
const getEstimatedRelineDate = ({
  inspectionDate,
  installDate,
  runHours,
  remainingHours
}) => {
  try {
    const insp = new Date(inspectionDate);
    const inst = new Date(installDate);

    // Anchor both dates at 12:00 PM
    insp.setHours(12, 0, 0, 0);
    inst.setHours(12, 0, 0, 0);

    // elapsed calendar hours between install & inspection
    const elapsedMs   = insp - inst;
    const elapsedHrs  = elapsedMs / (1000 * 60 * 60);

    // runtime versus calendar ratio
    const rate = elapsedHrs > 0 ? runHours / elapsedHrs : 0;

    // how many calendar hours remain
    const projCalendarHrs = rate > 0 ? remainingHours / rate : 0;

    // add projected hours back to inspection
    const projMs     = projCalendarHrs * 3600 * 1000;
    const estDate    = new Date(insp.getTime() + projMs);

    // re-anchor to 12:00 PM to avoid day-rollover
    estDate.setHours(12, 0, 0, 0);

    return estDate.toLocaleDateString('en-GB', {
      day:    '2-digit',
      month:  'short',
      year:   '2-digit'
    });
  } catch {
    return '—';
  }
};

function projectDateByRemainingHours(inspectionDate, remainingHours) {
  try {
       const baseMs   = new Date(inspectionDate).getTime();
      const addMs    = remainingHours * 3600 * 1000;
      const projected = new Date(baseMs + addMs);

      return projected.toLocaleDateString('en-GB', {
        day:   '2-digit',
        month: 'short',
        year:  '2-digit'
      });
  } catch (e) {
    console.log("projectDateByRemainingHours ",e)
  }
}

const WearSummaryTable = ({ data, inspectionData, onRowsChange}) => {
  const rows = [];
  const tableContainerRef = useRef(null);
  Object.entries(data).forEach(([location, entry]) => {
    // only FEH outer for now
   // if (location !== 'fehOuter') return;
    if (!entry?.p1) return;

    // —— Extract chart drop
    const series      = entry.chartData?.datasets?.[1]?.data || [];
    const pinCount    = getPinCount(safeNumber(entry.lifterWidth));
    const dropAtPin   = safeNumber(series[pinCount]?.y);

    const dropAtPinPlate = Math.min(safeNumber(series[1]?.y), safeNumber(series[2]?.y));

    // —— Lifter calculations
    const nominalL    = safeNumber(entry.lifterHeight) ;
    const relineL     = safeNumber(entry.relineLimitLifter);
    const availL      = nominalL - relineL;
    const droppedL    = nominalL + safeNumber(entry.sublining) - dropAtPin;
    const remainHgtL  = availL - droppedL;
    const runHrsL     = safeNumber(entry.totalRunningHoursLifter);
    const tonsL       = safeNumber(entry.totalTonsProcessedLifter);
    const wrHrL       = runHrsL  > 0 ? droppedL / runHrsL  : 0;
    const wrTonL      = tonsL   > 0 ? droppedL / tonsL  * 1000 : 0;
    const remHrsL     = wrHrL   > 0 ? remainHgtL / wrHrL : 0;
    const remTnsL     = wrTonL  > 0 ? remainHgtL / wrTonL : 0;
    const totalHrsL   = runHrsL + remHrsL;
    const totalTnsL   = tonsL   + remTnsL;
    const estRelineL  = getEstimatedRelineDate({
      inspectionDate:    inspectionData?.inspection_date,
      installDate:       entry.installationDateLifter,
      runHours:          runHrsL,
      remainingHours:    remHrsL
    });
    const estRelineL100 = projectDateByRemainingHours(inspectionData?.inspection_date, remHrsL);

    rows.push({
      location:             location.toUpperCase(),
      component:            'LIFTER',
      measuredDistance:     entry.measuredDistanceFromFeDe || '—',
      installDate:          formatDate(entry.installationDateLifter),
      runHours:             runHrsL,
      tonsProcessed:        tonsL,
      remainingLife:        availL > 0 
                             ? ((remainHgtL / availL) * 100).toFixed(2)
                             : '—',
            wearRateHr   :wrHrL.toFixed(4),
      wearRate:             wrTonL.toFixed(4),
      estimatedReline:      estRelineL,
      nominalHeight:        nominalL,
      relineHeight:         relineL,
      availableHeight:      availL.toFixed(2),
      heightDropped:        droppedL.toFixed(2),
      remainingHeight:      remainHgtL.toFixed(2),
      remainingHours:       remHrsL.toFixed(2),
      remainingTons:        remTnsL.toFixed(2),
      totalProjectedLifeTons: totalTnsL.toFixed(2),
      totalProjectedLifeHours: totalHrsL.toFixed(2),
      estimatedReline100: estRelineL100
    });

    // —— Plate calculations
    const nominalP    = safeNumber(entry.plateThicknessLeading);
    const relineP     = safeNumber(entry.relineLimitPlate);
    const availP      = nominalP - relineP;
    const droppedP    = nominalP + safeNumber(entry.sublining) - dropAtPinPlate; // assume full drop
    const remainHgtP  = availP - droppedP;
    const runHrsP     = safeNumber(entry.totalRunningHoursPlate);
    const tonsP       = safeNumber(entry.totalTonsProcessedPlate);
    const wrHrP       = runHrsP > 0 ? droppedP / runHrsP : 0;
    const wrTonP      = tonsP   > 0 ? droppedP / tonsP   : 0;
    const remHrsP     = wrHrP   > 0 ? remainHgtP / wrHrP : 0;
    const remTnsP     = wrTonP  > 0 ? remainHgtP / wrTonP : 0;
    const totalHrsP   = runHrsP + remHrsP;
    const totalTnsP   = tonsP   + remTnsP;
    const estRelineP  = getEstimatedRelineDate({
      inspectionDate:    inspectionData?.inspection_date,
      installDate:       entry.installationDatePlate,
      runHours:          runHrsP,
      remainingHours:    remHrsP
    });
    const estRelineP100 = projectDateByRemainingHours(inspectionData?.inspection_date, remHrsP);

    rows.push({
      location:             location.toUpperCase(),
      component:            'PLATE',
      measuredDistance:     entry.measuredDistanceFromFeDe || '—',
      installDate:          formatDate(entry.installationDatePlate),
      runHours:             runHrsP,
      tonsProcessed:        tonsP,
      remainingLife:        availP > 0
                             ? ((remainHgtP / availP) * 100).toFixed(2)
                             : '—',
      wearRateHr   :        wrHrP.toFixed(4),
      wearRate:             wrTonP.toFixed(4),
      estimatedReline:      estRelineP,
      nominalHeight:        nominalP,
      relineHeight:         relineP,
      availableHeight:      availP.toFixed(2),
      heightDropped:        droppedP.toFixed(2),
      remainingHeight:      remainHgtP.toFixed(2),
      remainingHours:       remHrsP.toFixed(2),
      remainingTons:        remTnsP.toFixed(2),
      totalProjectedLifeTons: totalTnsP.toFixed(2),
      totalProjectedLifeHours: totalHrsP.toFixed(2),
      estimatedReline100: estRelineP100
    });
  });


  // ... build rows exactly as you already do (push lifter and plate objects)

  // notify paren
  // t whenever rows change
  useEffect(() => {




    if (typeof onRowsChange === 'function') {
      try {
        const payload = JSON.parse(JSON.stringify(rows));
        onRowsChange(payload);
      } catch (e) {
        onRowsChange(rows);
      }
    }
  }, [JSON.stringify(rows), onRowsChange]); // stringify rows so dependency tracks deep changes

useEffect(() => {
  const handleClickOutside = (event) => {
    if (
      tableContainerRef.current &&
      !tableContainerRef.current.contains(event.target)
    ) {
      // Scroll to the left
      tableContainerRef.current.scrollLeft = 0;
    }
  };

  document.addEventListener('click', handleClickOutside);
  return () => {
    document.removeEventListener('click', handleClickOutside);
  };
}, []);

  return (
    <div className="wear-table-container" ref={tableContainerRef}>
      {/* <h3>FEED END HEAD</h3> */}
      <table className="wear-summary-table">
        <thead>
          <tr>
            <th>LOCATION</th>
            <th>COMPONENT</th>           
            <th>
              MEASURED<br/>
               DISTANCE
            </th>
            <th>INSTALL DATE</th>
            <th>RUN HOURS</th>
            <th>TONS PROCESSED</th>
            <th>REMAINING LIFE [%]</th>
             <th>WEAR RATE [mm/Hr]</th>
            <th>WEAR RATE[mm/T.ton]</th>  
            <th>ESTIMATED RELINE</th>
            <th>ESTIMATED RELINE[on 100%]</th>
            <th>NOMINAL <br/> HEIGHT</th>
            <th>RELINE  <br/> HEIGHT</th>
            <th>AVAILABLE HEIGHT</th>
            <th>HEIGHT DROPPED</th>
            <th>REMAINING HEIGHT</th>
            <th>REMAINING HOURS</th>
            <th>REMAINING TONS</th>
            <th>TOTAL LIFE [TONS]</th>
            <th>TOTAL LIFE [HOURS]</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => 
            Math.abs(r.heightDropped) > 0 ? (
                      
                <tr key={i}>
                  <td  rowSpan={1}>{r.location}</td>
                  <td>{r.component}</td>
                  <td  rowSpan={1}>{r.measuredDistance}</td>                  
                  <td>{r.installDate}</td>
                  <td>{r.runHours}</td>
                  <td>{r.tonsProcessed}</td>
                  <td>{r.remainingLife}</td>
                  <td>{r.wearRateHr}</td>
                  <td>{r.wearRate}</td>
                  <td>{r.estimatedReline}</td>
                  <td>{r.estimatedReline100}</td>
                  <td>{r.nominalHeight}</td>
                  <td>{r.relineHeight}</td>
                  <td>{r.availableHeight}</td>
                  <td>{r.heightDropped}</td>
                  <td>{r.remainingHeight}</td>
                  <td>{r.remainingHours}</td>
                  <td>{r.remainingTons}</td>
                  <td>{r.totalProjectedLifeTons}</td>
                  <td>{r.totalProjectedLifeHours}</td>
                    
                </tr>
                
                ) : null 
           )}
        </tbody>
      </table>
    </div>
  );
};

export default WearSummaryTable;
